import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import classNames from 'clsx';

import { DESIGN_INLINE, DESIGN_INLINE_VERTICAL } from '../../../constants/carousels';
import { channelPropTypes, videoPropTypes } from '@/modules/common/constants/propTypes';

import { useSmallScreen } from '@/modules/common/helpers/customHooks';
import { getFileUrlByDimensions } from '@/modules/common/helpers/file';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import Image from '../../../../common/components/Image/Image';
import VideoInfo from '../../../../common/components/VideoInfo/VideoInfo';
import VideoThumbnail from '../../../../common/components/VideoThumbnail/VideoThumbnail';
import SlideSkeleton from './SlideSkeleton';
import { BrowserLink, Link, TruncatedText, Typography } from '@/mui/components';

import styles from './Slide.module.scss';

function Slide({ isActive = false, preloadThumbnail = false, lazyLoading = false, ...props }) {
  const location = useLocation();
  const isSmallScreen = useSmallScreen();
  const [isHovered, setHoverState] = useState(false);

  const isInlineTypeSlide = [DESIGN_INLINE, DESIGN_INLINE_VERTICAL].includes(props.channel.design);

  return (
    <Link
      color="inherit"
      className={styles.Slide}
      component={BrowserLink}
      to={`/channel/${props.channel.id}/${props.video.mediaid}${location.search}`}
      onClick={(event) => {
        if (props.onSlideClickFn) {
          props.onSlideClickFn(event, props.video.mediaid);
        }

        if (props.onSlideFocusFn) {
          props.onSlideFocusFn();
        }
      }}
      onDoubleClick={(event) => {
        if (props.onSlideDoubleClickFn) {
          props.onSlideClickFn(event, props.video.mediaid);
        }
      }}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onMouseEnter={() => setHoverState(true)}
      onMouseLeave={() => setHoverState(false)}
      onFocus={props.onSlideFocusFn}
    >
      <div
        className={classNames(styles.SlideContainer, {
          [styles.SlideContainer___inline]: isInlineTypeSlide,
          [styles.SlideContainer___inlineActive]: isInlineTypeSlide && isActive,
        })}
        style={{
          height: props.height,
        }}
      >
        {isActive && props.activeCustomContent ? (
          props.activeCustomContent
        ) : (
          <>
            <Image
              src={getFileUrlByDimensions(props.video.images, props.width, props.height)}
              cover
              initialPreload={preloadThumbnail}
              width={props.width}
              height={props.height}
              lazyLoading={lazyLoading}
            />
            {!isInlineTypeSlide && !isMobileUserAgent && (
              <>
                <div className={styles.VideoThumbnail}>
                  <VideoThumbnail previewUrl={props.video.previewUrl} isHovered={isHovered} />
                </div>
                <VideoInfo
                  description={props.video.plot}
                  date={props.video.createdFormatted}
                  duration={props.video.durationFormatted}
                  isHovered={isHovered}
                  rows={isSmallScreen ? 1 : 3}
                />
              </>
            )}
          </>
        )}
        {props.channel.design === DESIGN_INLINE && isActive && (
          <Typography variant="caption" component="div" className={styles.PlayIndicator} data-a="play-indicator">
            NOW PLAYING
          </Typography>
        )}
      </div>
      <div className={styles.SlideTitle} data-a="video-title">
        <TruncatedText title={props.video.title} rows={1}>
          {props.video.title}
        </TruncatedText>
      </div>
    </Link>
  );
}

Slide.propTypes = {
  isActive: PropTypes.bool,
  channel: channelPropTypes.isRequired,
  video: videoPropTypes.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  activeCustomContent: PropTypes.node,
  onSlideClickFn: PropTypes.func,
  onSlideDoubleClickFn: PropTypes.func,
  onSlideFocusFn: PropTypes.func,
  preloadThumbnail: PropTypes.bool,
  lazyLoading: PropTypes.bool,
  onEnded: PropTypes.func,
};

export const Skeleton = SlideSkeleton;

export default Slide;
